import { Injectable } from '@angular/core';
import { DossierSearchType, GridType } from '@app/pcs/models';
import { getDossierSearchType, getSelectedFilter, getUserGridConfiguration, getUserGridType } from '@app/pcs/reducer';
import { FilterDisplaySettingsService } from '@app/pcs/services/filter-display-settings.service';
import { UserGridConfigurationService } from '@app/pcs/services/user-grid-configuration.service';
import * as fromRoot from '@app/reducer';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { hasNoValue } from '@util';
import {
  DataGridColumnWithId,
  getGridColumns,
  reorderColumn,
  resizeColumn,
  toggleColumnVisibility,
  updateFilters,
  updateSort
} from 'ngrx-data-grid';
import * as R from 'ramda';
import { exhaustMap } from 'rxjs/operators';

const isSearchGrid = R.propEq('name', GridType.Search);

const updateForFilter = (searchType, selectedFilter) => searchType === DossierSearchType.FILTER && selectedFilter;

const findColumn = (columnId: string, columns: DataGridColumnWithId[]): DataGridColumnWithId =>
  R.find<DataGridColumnWithId>(R.propEq('columnId', columnId), columns);

@Injectable()
export class UserGridConfigurationEffects {
  gridState$ = this.store.pipe(select(fromRoot.getGridState));
  gridSearchColumns$ = this.gridState$.pipe(select(getGridColumns, {gridName: GridType.Search}));
  gridDisplayColumns$ = this.gridState$.pipe(select(getGridColumns, {gridName: GridType.Display}));
  selectedFilter$ = this.store.pipe(select(getSelectedFilter));
  searchType$ = this.store.pipe(select(getDossierSearchType));
  userGrid$ = this.store.pipe(select(getUserGridType));
  userGridConfiguration$ = this.store.pipe(select(getUserGridConfiguration));

  updateColumns$ = createEffect(() => this.actions$.pipe(
    ofType(toggleColumnVisibility, updateFilters, updateSort, resizeColumn),
    concatLatestFrom((action) => [
      isSearchGrid(action) ? this.gridSearchColumns$ : this.gridDisplayColumns$,
      this.searchType$,
      this.selectedFilter$,
      this.userGrid$,
      this.userGridConfiguration$
    ]),
    exhaustMap(([action, columns, searchType, selectedFilter, userGridType, gridConfig]: any) => {
      const updatedColumn = findColumn(action.columnId, columns);
      return hasNoValue(userGridType) && updateForFilter(searchType, selectedFilter)
        ? this.filterDisplaySettingsService.updateForColumn(updatedColumn, selectedFilter)
        : this.userGridConfigurationService.updateForColumn(updatedColumn, userGridType, gridConfig);
    })
  ));

  reorderColumn$ = createEffect(() => this.actions$.pipe(
    ofType(reorderColumn),
    concatLatestFrom((action) => [
      isSearchGrid(action) ? this.gridSearchColumns$ : this.gridDisplayColumns$,
      this.searchType$,
      this.selectedFilter$,
      this.userGrid$,
      this.userGridConfiguration$
    ]),
    exhaustMap(([_, columns, searchType, selectedFilter, userGridType, gridConfig]: any) =>
      hasNoValue(userGridType) && updateForFilter(searchType, selectedFilter)
        ? this.filterDisplaySettingsService.updateForColumns(columns, selectedFilter)
        : this.userGridConfigurationService.updateForColumns(columns, userGridType, gridConfig)
    )
  ));

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private filterDisplaySettingsService: FilterDisplaySettingsService,
    private userGridConfigurationService: UserGridConfigurationService
  ) {
  }
}
